
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AuthContext, type AuthContextValue } from "@/providers/auth-context";
import type { User, LoginRequest, RegisterRequest } from "@/lib/types";
import * as api from "@/lib/api";
import { getToken, setTokens, clearTokens } from "@/lib/auth";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore identity from the server, including verification and current role.
  useEffect(() => {
    let cancelled = false;
    async function restore() {
      try {
        if (getToken()) {
          const currentUser = await api.getCurrentUser();
          if (!cancelled) setUser(currentUser);
        }
      } catch {
        if (!cancelled) clearTokens();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void restore();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (data: LoginRequest) => {
    const authResp = await api.login(data);
    setTokens(authResp.access_token, authResp.refresh_token);
    setUser(authResp.user);
  }, []);

  const register = useCallback(async (data: RegisterRequest) => {
    await api.register(data);
  }, []);

  const logout = useCallback(() => {
    // Fire-and-forget server-side revocation; clearTokens runs inside api.logout.
    void api.logout().catch(() => clearTokens());
    setUser(null);
  }, []);

  const refreshTokenFn = useCallback(async () => {
    const authResp = await api.refreshToken();
    setTokens(authResp.access_token, authResp.refresh_token);
    setUser(authResp.user);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      login,
      register,
      logout,
      refreshToken: refreshTokenFn,
    }),
    [user, loading, login, register, logout, refreshTokenFn],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
